import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { X, CalendarCheck, Plane, AlertTriangle, Loader2 } from 'lucide-react';

export default function UserAuditModal({ user, onClose }) {
  const [activeTab, setActiveTab] = useState('reports');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    events: [],
    travelPosts: [],
    reports: []
  });

  useEffect(() => {
    if (!user) return;

    const fetchAuditData = async () => {
      setIsLoading(true);
      try {
        const [eventsRes, travelRes, reportsRes] = await Promise.all([
          supabase.from('events').select('id, title, created_at, date').eq('organizer_id', user.id).order('created_at', { ascending: false }),
          supabase.from('travel_posts').select('id, destination, created_at').eq('organizer_id', user.id).order('created_at', { ascending: false }),
          supabase.from('reports').select('id, reason, description, status, created_at').eq('reported_user_id', user.id).order('created_at', { ascending: false })
        ]);

        setData({
          events: eventsRes.data || [],
          travelPosts: travelRes.data || [],
          reports: reportsRes.data || []
        });
      } catch (err) {
        console.error('Fetch audit data error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditData();
  }, [user]);

  if (!user) return null;

  const TabButton = ({ id, label, icon: Icon, count }) => {
    const isActive = activeTab === id;
    return (
      <button
        onClick={() => setActiveTab(id)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px',
          background: isActive ? 'white' : 'transparent',
          border: 'none', borderBottom: isActive ? '2px solid #4e8bca' : '2px solid transparent',
          color: isActive ? '#4e8bca' : '#64748b',
          fontWeight: isActive ? 'bold' : 'normal',
          cursor: 'pointer', transition: 'all 0.2s'
        }}
      >
        <Icon size={16} /> {label} <span style={{ background: isActive ? '#e6f2f0' : '#f1f5f9', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{count}</span>
      </button>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', width: '100%', maxWidth: '600px',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>用戶紀錄審查</h2>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>{user.name} ({user.email})</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '0 16px' }}>
          <TabButton id="reports" label="被檢舉紀錄" icon={AlertTriangle} count={data.reports.length} />
          <TabButton id="events" label="發起活動" icon={CalendarCheck} count={data.events.length} />
          <TabButton id="travelPosts" label="發起旅行" icon={Plane} count={data.travelPosts.length} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: '#f8fafc' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Loader2 className="animate-spin" color="#4e8bca" /></div>
          ) : (
            <>
              {activeTab === 'reports' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {data.reports.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8' }}>無檢舉紀錄</p> : 
                    data.reports.map(r => (
                      <div key={r.id} style={{ background: 'white', padding: '16px', borderRadius: '12px', borderLeft: `4px solid ${r.status === 'pending' ? '#ef4444' : '#10b981'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <strong style={{ color: '#1e293b' }}>{r.reason}</strong>
                          <span style={{ fontSize: '12px', background: r.status === 'pending' ? '#fee2e2' : '#d1fae5', color: r.status === 'pending' ? '#dc2626' : '#059669', padding: '2px 8px', borderRadius: '12px' }}>
                            {r.status}
                          </span>
                        </div>
                        <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#475569' }}>{r.description || '(無詳細說明)'}</p>
                        <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  }
                </div>
              )}

              {activeTab === 'events' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {data.events.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8' }}>無發起紀錄</p> : 
                    data.events.map(e => (
                      <div key={e.id} style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <strong style={{ color: '#1e293b' }}>{e.title}</strong>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>活動日期: {new Date(e.date).toLocaleDateString()}</p>
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#94a3b8' }}>建立於: {new Date(e.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  }
                </div>
              )}

              {activeTab === 'travelPosts' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {data.travelPosts.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8' }}>無旅行貼文</p> : 
                    data.travelPosts.map(p => (
                      <div key={p.id} style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <strong style={{ color: '#1e293b' }}>{p.destination}</strong>
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#94a3b8' }}>建立於: {new Date(p.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  }
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
